/**
 * Game Favorite Composable
 * 管理遊戲收藏功能（使用樂觀更新策略）
 * 原始來源: cuora-module/src/machine/game_favorite_v2.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/game/gameFavorite.js)
 * - 業務邏輯：保留在 composable 中
 * - API 呼叫：在此層處理
 * - 樂觀更新：先更新 UI，API 失敗時回滾
 */

import { storeToRefs } from 'pinia'

import { gameApi } from '@/api'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useGameFavoriteStore } from '@/stores/game/gameFavorite'
import { useMainStore } from '@/stores/main'

export const useGameFavorite = () => {
  const { $request } = useNuxtApp()
  const store = useGameFavoriteStore()
  const mainStore = useMainStore()
  const { isGuest } = useAuthToken()

  const { favorites, favoriteList, hasFavorite } = storeToRefs(store)

  /**
   * 獲取收藏列表
   */
  const fetchFavorites = async () => {
    try {
      const data = await $request.send(gameApi.getListMyFavorite({ timestamp: null }))
      store.setFavorites(data.data || [])
    } catch (err) {
      store.setFavorites([])
      console.error('[useGameFavorite] Fetch favorites error:', err)
    }
  }

  /**
   * 添加收藏（樂觀更新）
   */
  const addFavorite = async (game) => {
    // 未登入則打開登入註冊彈窗
    if (isGuest.value) {
      mainStore.setModal('ModalGuestLogin')
      return
    }

    // 防呆檢查：game 必須有效且包含 gameCode
    if (!game?.gameCode) {
      console.warn('[useGameFavorite] Invalid game object:', game)
      return
    }

    // 防呆檢查：已經收藏則直接返回
    if (hasFavorite.value(game.gameCode)) {
      return
    }

    // 保存快照用於回滾
    const snapshot = [...favorites.value]

    // 樂觀更新：立即添加到 UI
    store.addFavorite(game)

    try {
      // 發送 API 請求
      await $request.send(gameApi.addListFavorite(game.gameCode))
    } catch (err) {
      // 失敗時回滾到原始狀態
      store.setFavorites(snapshot)
      console.error('[useGameFavorite] Add favorite error:', err)
      throw err
    }
  }

  /**
   * 移除收藏（樂觀更新）
   */
  const removeFavorite = async (game) => {
    // 未登入則打開登入註冊彈窗
    if (isGuest.value) {
      mainStore.setModal('ModalGuestLogin')
      return
    }

    const gameCode = game?.gameCode || game

    // 防呆檢查：gameCode 必須有效
    if (!gameCode) {
      console.warn('[useGameFavorite] Invalid game or gameCode:', game)
      return
    }

    // 防呆檢查：未收藏則直接返回
    if (!hasFavorite.value(gameCode)) {
      return
    }

    // 保存快照用於回滾
    const snapshot = [...favorites.value]

    // 樂觀更新：立即從 UI 移除
    store.removeFavorite(gameCode)

    try {
      // 發送 API 請求
      await $request.send(gameApi.removeListFavorite(gameCode))
    } catch (err) {
      // 失敗時回滾到原始狀態
      store.setFavorites(snapshot)
      console.error('[useGameFavorite] Remove favorite error:', err)
      throw err
    }
  }

  /**
   * 清空收藏列表（本地）
   */
  const clearFavorites = () => {
    store.clearFavorites()
  }

  return {
    // State
    favorites,
    favoriteList,
    hasFavorite,
    // Actions
    fetchFavorites,
    addFavorite,
    removeFavorite,
    clearFavorites,
  }
}
